/** Backup tab — what the archive holds, and retrying a failed upload.
 *
 * Routes (backend, app/api/archive.py):
 *   GET  /api/archive/sessions?days=30
 *   POST /api/archive/sessions/{trade_date}/sync
 */

import { getJson } from "@/lib/http";

/** Where a session has got to.
 *
 * `pending` is today before 16:10 — never an error, and the retry button
 * stays disabled for it. `not_captured` on a trading day is the serious
 * one: the broker window has closed and those bars cannot be recovered.
 * `missing` and `partial` are only failed UPLOADS — the rows are safe in
 * Postgres and a retry can fix them. */
export type SessionState = "synced" | "partial" | "missing" | "pending" | "not_captured";

export interface ArchiveFile {
  name: string;
  underlying: string;
  kind: "options" | "index";
  bars: number;
  /** Option bars with all five greeks solved; always 0 for index files. */
  greeks: number;
  on_disk: boolean;
  /** ⚠️ A RECEIPT, not a live check. The VPS key is PutObject-only, so the
   * backend records what it uploaded rather than asking S3. True means "we
   * have evidence we sent this", not "I just confirmed it is there". */
  in_s3: boolean;
  size_bytes: number | null;
  uploaded_at: string | null;
}

export interface ArchiveSession {
  trade_date: string;
  bars: number;
  greeks: number;
  files: ArchiveFile[];
  state: SessionState;
  /** True only when a retry could actually change something. */
  can_sync: boolean;
}

export interface ArchiveSessions {
  sessions: ArchiveSession[];
  bucket: string | null;
  dump_time_ist: string;
}

export interface SyncResult {
  trade_date: string;
  ok: boolean;
  uploaded: string[];
  /** Verbatim store errors — an operator needs "AccessDenied (PutObject)",
   * not "sync failed". */
  errors: string[];
  message: string;
}

export const archiveApi = {
  sessions(days = 30): Promise<ArchiveSessions> {
    return getJson<ArchiveSessions>(`/api/archive/sessions?days=${String(days)}`);
  },
  sync(tradeDate: string): Promise<SyncResult> {
    return getJson<SyncResult>(`/api/archive/sessions/${tradeDate}/sync`, { method: "POST" });
  },
  /** A plain href, deliberately — not a fetch.
   *
   * Letting the browser follow the link streams the gzip straight to disk
   * and gives a progress bar for free; fetching it would buffer the whole
   * file in the tab first. Same-origin behind Caddy, so the session cookie
   * rides along and no token handling is needed. */
  downloadUrl(file: Pick<ArchiveFile, "name">): string {
    return `/api/archive/files/${file.name.split("/").map(encodeURIComponent).join("/")}`;
  },
};
